import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ScanAPI,
  Scan,
  CreateScanData,
  UpdateScanData,
  FindScansOptions,
  ScanComponent,
  ScanVulnerability,
  ScanLicenseIssue,
  ScanTrendData,
  Pagination
} from '../api';

export function useScans(options?: FindScansOptions) {
  const [data, setData] = useState<Scan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>();

  const fetchScans = async () => {
    try {
      setLoading(true);
      const response = await ScanAPI.getAll(options);

      if (response.code === '200' && response.data) {
        setData(response.data as any);
        setPagination({
          totalItems: response.totalNoOfItem || 0,
          page: response.page || 1,
          perPage: response.perPage || 10,
        });
      } else {
        setError(response.message || 'Failed to fetch scans');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScans();
  }, [options?.projectId, options?.applicationId, options?.workgroupId]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchScans
  };
}

export function useScan(id: number) {
  const [data, setData] = useState<Scan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { activeWorkgroupId } = useAuth();

  const fetchScan = async () => {
    try {
      setLoading(true);
      const response = await ScanAPI.getById(id);

      if (response.code === '200' && response.data) {
        if (response.data.workgroupid !== activeWorkgroupId) {
          setError('Access denied: Scan does not belong to your workgroup');
          setData(null);
          return;
        }
        setData(response.data);
      } else {
        setError(response.message || 'Failed to fetch scan');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchScan();
    }
  }, [id, activeWorkgroupId]);

  return { data, loading, error, refetch: fetchScan };
}

export function useScanComponents(scanId: number) {
  const [data, setData] = useState<ScanComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchComponents() {
      if (!scanId) return;

      try {
        setLoading(true);
        const response = await ScanAPI.getComponents(scanId);

        if (response.code === '200' && response.data) {
          setData(response.data);
        } else {
          setError(response.message || 'Failed to fetch scan components');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchComponents();
  }, [scanId]);

  return { data, loading, error };
}

export function useScanVulnerabilities(scanId: number) {
  const [data, setData] = useState<ScanVulnerability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchVulnerabilities() {
      if (!scanId) return;

      try {
        setLoading(true);
        const response = await ScanAPI.getVulnerabilities(scanId);

        if (response.code === '200' && response.data) {
          setData(response.data);
        } else {
          setError(response.message || 'Failed to fetch scan vulnerabilities');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchVulnerabilities();
  }, [scanId]);

  return { data, loading, error };
}

export function useScanLicenseIssues(scanId: number) {
  const [data, setData] = useState<ScanLicenseIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchLicenseIssues() {
      if (!scanId) return;

      try {
        setLoading(true);
        const response = await ScanAPI.getLicenseIssues(scanId);

        if (response.code === '200' && response.data) {
          setData(response.data);
        } else {
          setError(response.message || 'Failed to fetch scan license issues');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchLicenseIssues();
  }, [scanId]);

  return { data, loading, error };
}


export function useLatestSuccessfulScan(projectId: number) {
  const [data, setData] = useState<Scan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchLatestSuccessfulScan() {
      if (!projectId) return;

      try {
        setLoading(true);
        const response = await ScanAPI.getLatestSuccessfulScanByProject(projectId);

        if (response.code === '200' && response.data) {
          setData(response.data);
        } else {
          setError(response.message || 'Failed to fetch latest successful scan');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    fetchLatestSuccessfulScan();
  }, [projectId]);

  return { data, loading, error };
}

export function useScanMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createScan = async (data: CreateScanData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ScanAPI.create(data);

      if (response.code === '201' || response.code === '200') {
        return response.data;
      } else {
        setError(response.message || 'Failed to create scan');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateScan = async (id: number, data: UpdateScanData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ScanAPI.update(id, data);

      if (response.code === '200') {
        return response.data;
      } else {
        setError(response.message || 'Failed to update scan');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const cancelScan = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await ScanAPI.cancel(id);

      if (response.code === '200') {
        return response.data;
      } else {
        setError(response.message || 'Failed to cancel scan');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    createScan,
    updateScan,
    cancelScan,
    loading,
    error,
  };
}
